import { useEffect, useRef, useState } from "react";

/**
 * Whether a loading state has been pending long enough to be worth showing —
 * and, once shown, long enough to be readable.
 *
 * An events fetch takes 176–267 ms (measured). A skeleton that appears the
 * instant a request starts would flash and vanish before anyone reads it, on
 * every jump, which is worse than showing nothing: the operator learns only that
 * the interface flickers. So nothing appears until the wait is genuinely
 * noticeable, and once something has appeared it stays put long enough to be
 * read rather than strobing off a beat later.
 */

/**
 * Below this, no indicator is shown at all.
 *
 * Sits ABOVE the measured fetch (176–267 ms) on purpose: at 180 ms the band
 * appeared for a blink on every ordinary fetch and then had to be held for its
 * minimum, which is a flash rather than information. Only a fetch that is
 * genuinely slower than normal earns an indicator.
 */
export const LOADING_DELAY_MS = 320;
/** Once shown, hold it at least this long, so a late arrival does not strobe. */
export const LOADING_MIN_VISIBLE_MS = 400;

/**
 * The state machine, pure so the timing rules are testable without a clock.
 *
 * Returns the next visibility and when it changed, given the current pending
 * state. `shownSince` is null while hidden.
 */
export const nextLoadingVisibility = (
  pending: boolean,
  pendingSince: number | null,
  shownSince: number | null,
  now: number,
): { visible: boolean; shownSince: number | null } => {
  if (pending) {
    if (shownSince != null) return { visible: true, shownSince };
    const waited = pendingSince == null ? 0 : now - pendingSince;
    return waited >= LOADING_DELAY_MS
      ? { visible: true, shownSince: now }
      : { visible: false, shownSince: null };
  }
  // Settled. Keep it up until it has had its minimum time on screen.
  if (shownSince != null && now - shownSince < LOADING_MIN_VISIBLE_MS) {
    return { visible: true, shownSince };
  }
  return { visible: false, shownSince: null };
};

/** React wrapper: `true` when a loading indicator should currently be drawn. */
export const useDelayedLoading = (pending: boolean): boolean => {
  const [visible, setVisible] = useState(false);
  const pendingSinceRef = useRef<number | null>(null);
  const shownSinceRef = useRef<number | null>(null);

  if (pending && pendingSinceRef.current == null) pendingSinceRef.current = Date.now();
  if (!pending) pendingSinceRef.current = null;

  useEffect(() => {
    const settle = () => {
      const result = nextLoadingVisibility(
        pending,
        pendingSinceRef.current,
        shownSinceRef.current,
        Date.now(),
      );
      shownSinceRef.current = result.shownSince;
      setVisible(result.visible);
    };
    settle();
    // One timer for whichever threshold is next: the delay before showing, or
    // the minimum duration before hiding.
    const wait = pending ? LOADING_DELAY_MS : LOADING_MIN_VISIBLE_MS;
    const id = setTimeout(settle, wait);
    return () => clearTimeout(id);
  }, [pending]);

  return visible;
};
